import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { HiOutlinePlus as AddIcon } from 'react-icons/hi2';
import { colors, fonts, spacing, borderRadius, shadows } from '../constants';
import { useProjectsPageState } from '../hooks/pages/projects/useProjectsPageState';
import { useProjectsPageData } from '../hooks/pages/projects/useProjectsPageData';
import { useProjectsPageForm } from '../hooks/pages/projects/useProjectsPageForm';
import { getStatusColor } from '../utils/projectsUtils';
import ProjectsTable from '../components/projectsPage/ProjectsTable';
import ProjectSearchBar from '../components/projectsPage/ProjectSearchBar';
import AddProjectDialog from '../components/projectsPage/AddProjectDialog';

const Projects = () => {
  // Initialize all hooks
  const pageState = useProjectsPageState();
  
  const { fetchProjects } = useProjectsPageData({
    page: pageState.page,
    rowsPerPage: pageState.rowsPerPage,
    searchQuery: pageState.searchQuery,
    setProjects: pageState.setProjects,
    setTotalProjects: pageState.setTotalProjects,
    setLoading: pageState.setLoading,
  });

  const { handleSubmit } = useProjectsPageForm({
    formData: pageState.formData,
    fetchProjects,
    handleCloseDialog: pageState.handleCloseDialog,
    setSubmitLoading: pageState.setSubmitLoading,
  });

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: spacing.md,
          gap: { xs: spacing.sm, sm: 0 }
        }}>
          <Typography variant="body1" sx={{ 
            color: colors.textSecondary, 
            fontSize: { xs: fonts.fontSizeSm, sm: fonts.fontSizeMd },
            mb: { xs: spacing.xs, sm: 0 }
          }}>
            Manage projects and project settings.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
            onClick={pageState.handleOpenDialog}
            fullWidth={window.innerWidth < 600}
            sx={{
              background: colors.primaryGradient,
              color: colors.textWhite,
              textTransform: 'none',
              borderRadius: borderRadius.sm,
              px: { xs: spacing.sm, sm: spacing.md },
              py: spacing.xs,
              fontWeight: fonts.weightSemiBold,
              fontSize: { xs: fonts.fontSizeSm, sm: fonts.fontSizeSm },
              boxShadow: `0 12px 24px ${colors.primaryShadow}`,
              '&:hover': {
                background: colors.primaryGradientHover,
                boxShadow: `0 16px 32px ${colors.primaryShadow}`,
              },
            }}
          >
            Add Project
          </Button>
        </Box>

        {/* Search Bar */}
        <ProjectSearchBar
          searchQuery={pageState.searchQuery}
          onSearchChange={pageState.setSearchQuery}
          onClearSearch={pageState.handleClearSearch}
        />

        {/* Projects Table */}
        <ProjectsTable
          loading={pageState.loading}
          totalProjects={pageState.totalProjects}
          projects={pageState.projects}
          page={pageState.page}
          rowsPerPage={pageState.rowsPerPage}
          handleChangePage={pageState.handleChangePage}
          handleChangeRowsPerPage={pageState.handleChangeRowsPerPage}
          getStatusColor={getStatusColor}
        />
      </Box>

      {/* Add Project Dialog */}
      <AddProjectDialog
        open={pageState.openDialog}
        onClose={pageState.handleCloseDialog}
        formData={pageState.formData}
        onInputChange={pageState.handleInputChange}
        onSubmit={handleSubmit}
        submitLoading={pageState.submitLoading}
        getStatusColor={getStatusColor}
      />
    </Box>
  );
};

export default Projects;
